/**
 * Physicians Page
 * 
 * Table view of all physicians with search and filtering
 */

import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { physicianAPI, sharedPhysiciansAPI, apiHelpers } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import useDraftStore, { useSharedDraftStore, SHARED_DIRECTORY_SCOPE } from '../stores/draftStore'
import PhysicianEditModal from '../components/PhysicianEditModal'
import ImportPhysiciansModal from '../components/ImportPhysiciansModal'
import SharedDraftBar from '../components/SharedDraftBar'
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Badge
} from '../components/ui'
import {
  ComposableMap,
  Geographies,
  Geography
} from 'react-simple-maps'
import { Tooltip } from 'react-tooltip'

// US states topology for react-simple-maps (using local file)
const geoUrl = "/gz_2010_us_040_00_20m.json"

// Helper function to get state abbreviation from state name
const getStateAbbreviation = (stateName) => {
  const stateMap = {
    'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
    'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
    'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
    'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
    'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
    'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
    'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
    'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
    'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
    'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
    'District of Columbia': 'DC', 'Washington DC': 'DC', 'Puerto Rico': 'PR'
  }
  return stateMap[stateName] || stateName
}

// Frontend helper to calculate active licenses from raw license data
// Returns ALL valid licenses from ALL states (no limits)
const calculateActiveLicenses = (licenses) => {
  const activeLicenses = []
  
  if (licenses && typeof licenses === 'object') {
    // Process ALL states and collect ALL valid licenses
    for (const [rawStateCode, licenseData] of Object.entries(licenses)) {
      // Normalize state code to uppercase for consistent matching
      const stateCode = rawStateCode.toUpperCase()
      
      if (licenseData && typeof licenseData === 'object') {
        
        // Check for valid state license in this state
        if (licenseData.state?.license) {
          const license = licenseData.state.license.trim()
          const expiry = licenseData.state.expiry
          
          // Valid if has license number and not expired
          if (license && 
              license.toLowerCase() !== 'n/a' && 
              license.toLowerCase() !== 'n a') {
            
            const isExpired = expiry && new Date(expiry) <= new Date()
            
            if (!isExpired) {
              activeLicenses.push({
                state: stateCode,
                type: 'state',
                license: license,
                expiry: expiry
              })
              
            }
          }
        }
        
        // Check for valid DEA license in this state
        if (licenseData.DEA?.license) {
          const license = licenseData.DEA.license.trim()
          const expiry = licenseData.DEA.expiry
          
          // Valid if has license number and not expired
          if (license && 
              license.toLowerCase() !== 'n/a' && 
              license.toLowerCase() !== 'n a') {
            
            const isExpired = expiry && new Date(expiry) <= new Date()
            
            if (!isExpired) {
              activeLicenses.push({
                state: stateCode,
                type: 'DEA',
                license: license,
                expiry: expiry
              })
              
            }
          }
        }
      }
    }
  }
  
  return activeLicenses
}

/**
 * The physicians list, map and stats, for either source: a clinic's own
 * physicians (drafts publish in the clinic's releases), or core's shared
 * directory (`shared`: read from /api/shared-physicians, filtered here, drafts
 * go to the shared store and publish as shared-directory releases). Pay is
 * never shown here.
 */
export function PhysiciansDirectory({ shared = false, canEdit = false, basePath = '/physicians', title = 'Physicians', bare = false }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [physicians, setPhysicians] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState(null)
  const [pagination, setPagination] = useState({})

  // Get auth state to check clinic selection
  const { user, currentClinicId } = useAuthStore()

  // Roles that can edit configs (excludes viewer and data_entry); the shared
  // directory takes its own grant instead.
  const canEditConfigs = shared ? canEdit : user && ['super_admin', 'clinic_admin', 'physician_manager'].includes(user.role)

  // Get draft changes (`shared` is fixed for the life of the page, so the hook is stable)
  const draftStore = shared ? useSharedDraftStore : useDraftStore
  const { getChangesByType, updateChange, initialize } = draftStore()
  useEffect(() => { if (shared && user?.id) initialize(SHARED_DIRECTORY_SCOPE, user.id) }, [shared, user?.id, initialize])
  const sharedDraftProps = shared ? { draftStore: useSharedDraftStore, draftScope: SHARED_DIRECTORY_SCOPE, wholeRecord: true } : {}

  // Helper function to merge physicians with draft changes
  const mergePhysiciansWithDrafts = (dbPhysicians) => {
    const draftChanges = getChangesByType('physician')
    
    // Start with database physicians
    let mergedPhysicians = [...dbPhysicians]
    
    // Apply draft changes
    draftChanges.forEach(change => {
      if (change.operation === 'create') {
        // Add new draft physician
        mergedPhysicians.push({
          ...change.new_data,
          _isDraft: true,
          _draftOperation: 'create',
          _draftId: change.id
        })
      } else if (change.operation === 'update') {
        // Update existing physician with draft changes
        const index = mergedPhysicians.findIndex(p =>
          p._id === change.resource_id
        )
        if (index !== -1) {
          mergedPhysicians[index] = {
            ...mergedPhysicians[index],
            ...change.new_data,
            _isDraft: true,
            _draftOperation: 'update',
            _draftId: change.id
          }
        }
      } else if (change.operation === 'delete') {
        // Mark as deleted (or remove)
        mergedPhysicians = mergedPhysicians.filter(p => 
          p._id !== change.resource_id
        )
      }
    })
    
    return mergedPhysicians
  }

  // Search and filter state
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [titleFilter, setTitleFilter] = useState(searchParams.get('title') || 'all')
  const [statusFilter, setStatusFilter] = useState(searchParams.get('accept_new_patients') || 'all')
  const [sortBy, setSortBy] = useState(searchParams.get('sort_by') || 'status')
  const [sortOrder, setSortOrder] = useState(searchParams.get('sort_order') || 'asc')
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1)
  
  // Modal state for state-specific physician filtering
  const [isStateModalOpen, setIsStateModalOpen] = useState(false)
  const [selectedState, setSelectedState] = useState(null)
  const [statePhysicians, setStatePhysicians] = useState([])
  
  // Modal state for adding/editing physicians
  const [isPhysicianModalOpen, setIsPhysicianModalOpen] = useState(false)
  const [editingPhysician, setEditingPhysician] = useState(null)
  const [editingDraftId, setEditingDraftId] = useState(null)

  // Modal state for importing physicians from another clinic (super_admin only)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)

  // Open the edit modal for an unsaved draft-create physician (no _id yet,
  // so the detail page route doesn't work). Edits write back to the same draft.
  const openDraftPhysician = (physician) => {
    if (!physician?._draftId) return
    setEditingPhysician(physician)
    setEditingDraftId(physician._draftId)
    setIsPhysicianModalOpen(true)
  }

  // Handle state click to show physicians with licenses in that state
  const handleStateClick = (stateCode, stateName) => {
    if (!stateCode || !physicians.length) return
    
    // Filter physicians who have active licenses in this state
    const filteredPhysicians = physicians.filter(physician => {
      const activeLicenses = calculateActiveLicenses(physician.licenses)
      return activeLicenses.some(license => license.state === stateCode)
    })
    
    // Format physicians with detailed information for the modal
    const formattedPhysicians = filteredPhysicians.map(physician => {
      const activeLicenses = calculateActiveLicenses(physician.licenses)
      const stateLicenses = activeLicenses.filter(license => license.state === stateCode)
      const hasStateLicense = stateLicenses.some(license => license.type === 'state')
      const hasDEALicense = stateLicenses.some(license => license.type === 'DEA')
      
      // Get state-specific license info
      const stateLicense = stateLicenses.find(license => license.type === 'state')
      const deaLicense = stateLicenses.find(license => license.type === 'DEA')
      
      // Get physician status
      const status = getPhysicianStatus(physician)
      
      return {
        id: physician._id,
        fullName: `${physician.title || ''} ${physician.first_name} ${physician.last_name}`.trim(),
        title: physician.title,
        npi: physician.npi,
        status: status,
        acceptNewPatients: physician.accept_new_patients,
        acceptRefillPatients: physician.accept_refill_patients,
        minimizeScripts: physician.physician_config?.minimize_scripts,
        stateLicense: stateLicense?.license,
        stateLicenseExpiry: stateLicense?.expiry,
        deaLicense: deaLicense?.license,
        deaLicenseExpiry: deaLicense?.expiry,
        hasStateOnly: hasStateLicense && !hasDEALicense,
        hasDEA: hasDEALicense
      }
    })
    
    setSelectedState({ code: stateCode, name: stateName })
    setStatePhysicians(formattedPhysicians)
    setIsStateModalOpen(true)
  }

  // Calculate aggregated state coverage from all physicians
  // Use raw license data consistently everywhere
  const calculateStateCoverage = () => {
    const stateCoverage = {}
    
    physicians.forEach(physician => {
      // Use our frontend helper with raw license data
      const activeLicenses = calculateActiveLicenses(physician.licenses)
      
      // Debug removed - state coverage calculation
      
      activeLicenses.forEach(license => {
        const stateCode = license.state
        
        if (!stateCoverage[stateCode]) {
          stateCoverage[stateCode] = {
            physicianCount: 0,
            hasActiveLicenses: false,
            hasActiveDEA: false,
            hasActiveState: false,
            hasExpiring: false,
            hasExpired: false,
            countedPhysicians: []
          }
        }
        
        // Check if this physician already counted for this state
        const alreadyCounted = stateCoverage[stateCode].countedPhysicians.includes(physician._id)
        if (!alreadyCounted) {
          stateCoverage[stateCode].countedPhysicians.push(physician._id)
          stateCoverage[stateCode].physicianCount += 1
        }
        
        // Check expiration status
        const checkExpiration = (dateString) => {
          if (!dateString || dateString.trim() === '') return { expired: false, expiring: false, active: true }
          const expiryDate = new Date(dateString)
          const today = new Date()
          const ninetyDaysFromNow = new Date(today.getTime() + (90 * 24 * 60 * 60 * 1000))
          return {
            expired: expiryDate < today,
            expiring: expiryDate >= today && expiryDate <= ninetyDaysFromNow,
            active: expiryDate > today
          }
        }
        
        const expStatus = checkExpiration(license.expiry)
        
        // Track specific license types (priority: Active DEA > Active State > Expiring > Expired)
        if (license.type === 'DEA' && expStatus.active) {
          stateCoverage[stateCode].hasActiveDEA = true
          stateCoverage[stateCode].hasActiveLicenses = true
        } else if (license.type === 'state' && expStatus.active) {
          stateCoverage[stateCode].hasActiveState = true
          stateCoverage[stateCode].hasActiveLicenses = true
        } else if (expStatus.expiring) {
          stateCoverage[stateCode].hasExpiring = true
          stateCoverage[stateCode].hasActiveLicenses = true
        } else if (expStatus.expired) {
          stateCoverage[stateCode].hasExpired = true
          stateCoverage[stateCode].hasActiveLicenses = true
        }
      })
    })
    
    return stateCoverage
  }
  
  // Get color for state based on coverage
  // Priority: Active DEA > Active State > Expiring State > Expired State
  const getStateColor = (stateCode, stateCoverage) => {
    const coverage = stateCoverage[stateCode]
    if (!coverage || !coverage.hasActiveLicenses) return '#e5e7eb' // gray-200 - no coverage
    if (coverage.hasActiveDEA) return '#7c3aed' // purple-600 - has active DEA (highest priority)
    if (coverage.hasActiveState) return '#16a34a' // green-600 - has active state license
    if (coverage.hasExpiring) return '#eab308' // yellow-500 - has expiring licenses
    if (coverage.hasExpired) return '#dc2626' // red-600 - has expired licenses (lowest priority)
    return '#e5e7eb' // gray-200 - fallback
  }

  // Load physicians and recalculate stats when physicians change
  useEffect(() => {
    if (shared || currentClinicId) {
      loadPhysicians()
    }
  }, [currentPage, search, titleFilter, statusFilter, sortBy, sortOrder, currentClinicId, shared])

  // Recalculate stats whenever physicians data changes
  useEffect(() => {
    if (physicians.length > 0) {
      loadStats()
    }
  }, [physicians])

  // Update URL parameters when filters change
  useEffect(() => {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    if (titleFilter !== 'all') params.set('title', titleFilter)
    if (statusFilter !== 'all') params.set('accept_new_patients', statusFilter)
    if (sortBy !== 'status') params.set('sort_by', sortBy)
    if (sortOrder !== 'asc') params.set('sort_order', sortOrder)
    if (currentPage !== 1) params.set('page', currentPage)
    
    setSearchParams(params)
  }, [search, titleFilter, statusFilter, sortBy, sortOrder, currentPage, setSearchParams])

  const loadPhysicians = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = {
        page: currentPage,
        limit: 50, // Show more in table format
        ...(search && { search }),
        ...(titleFilter !== 'all' && { title: titleFilter }),
        ...(statusFilter !== 'all' && { accept_new_patients: statusFilter === 'true' }),
        // Only send sort params to backend if not sorting by status (handled client-side)
        ...(sortBy !== 'status' && { sort_by: sortBy, sort_order: sortOrder }),
      }

      let dbPhysicians
      let formattedResponse = { pagination: {} }
      if (shared) {
        // The directory is a few dozen records: fetch it whole and filter here
        // the way the API does for a clinic. `_id` aliases core's `id`.
        const response = await sharedPhysiciansAPI.list()
        const needle = search.toLowerCase()
        dbPhysicians = (response.data.data || [])
          .map(d => ({ ...d, _id: d.id }))
          .filter(d => !needle || [d.first_name, d.last_name, d.email, d.npi, d.prefix, d.physician_id].some(v => String(v || '').toLowerCase().includes(needle)))
          .filter(d => titleFilter === 'all' || d.title === titleFilter)
          .filter(d => statusFilter === 'all' || Boolean(d.accept_new_patients) === (statusFilter === 'true'))
        if (sortBy !== 'status') {
          const dir = sortOrder === 'desc' ? -1 : 1
          dbPhysicians.sort((a, b) => String(a[sortBy] ?? '').localeCompare(String(b[sortBy] ?? '')) * dir)
        }
      } else {
        const response = await physicianAPI.list(params)
        formattedResponse = apiHelpers.formatResponse(response)
        dbPhysicians = formattedResponse.data.physicians || []
      }

      // Merge with draft changes
      const physiciansWithDrafts = mergePhysiciansWithDrafts(dbPhysicians)
      setPhysicians(physiciansWithDrafts)
      setPagination(formattedResponse.pagination || {})
    } catch (err) {
      const errorInfo = apiHelpers.handleError(err)
      setError(errorInfo.message)
    } finally {
      setLoading(false)
    }
  }

  // Calculate stats from raw physician data consistently
  const calculateStatsFromRawData = () => {
    if (!physicians.length) return null
    
    let totalActiveLicenses = 0
    const activeStates = new Set()
    let expiringLicensesCount = 0
    
    physicians.forEach(physician => {
      const activeLicenses = calculateActiveLicenses(physician.licenses)
      totalActiveLicenses += activeLicenses.length
      
      // Track active states
      activeLicenses.forEach(license => {
        activeStates.add(license.state)
      })
      
      // Check for expiring licenses (within 90 days)
      const ninetyDaysFromNow = new Date()
      ninetyDaysFromNow.setDate(ninetyDaysFromNow.getDate() + 90)
      
      const hasExpiringLicense = activeLicenses.some(license => {
        if (license.expiry) {
          const expiryDate = new Date(license.expiry)
          return expiryDate <= ninetyDaysFromNow && expiryDate >= new Date()
        }
        return false
      })
      
      if (hasExpiringLicense) {
        expiringLicensesCount++
      }
    })
    
    const acceptingNewPatients = physicians.filter(p => p.accept_new_patients).length
    const acceptingRefills = physicians.filter(p => p.accept_refill_patients).length
    
    return {
      total_physicians: physicians.length,
      accepting_new_patients: acceptingNewPatients,
      accepting_refills: acceptingRefills,
      active_licenses: totalActiveLicenses,
      expiring_licenses_count: expiringLicensesCount,
      active_states_count: activeStates.size,
      active_states: Array.from(activeStates).sort()
    }
  }

  const loadStats = async () => {
    try {
      // Calculate stats from current physician data instead of API call
      const calculatedStats = calculateStatsFromRawData()
      setStats(calculatedStats)
    } catch (err) {
      console.error('🔍 [FRONTEND] Error calculating stats:', err)
      // Error calculating stats - continue silently
    }
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    setCurrentPage(1) // Reset to first page when searching
  }

  const resetFilters = () => {
    setSearch('')
    setTitleFilter('all')
    setStatusFilter('all')
    setSortBy('status')
    setSortOrder('asc')
    setCurrentPage(1)
  }

  const formatPhysicianName = (physician) => {
    const { first_name, middle_name, last_name } = physician
    return [first_name, middle_name, last_name].filter(Boolean).join(' ')
  }

  // Determine physician status category for styling
  const getPhysicianStatus = (physician) => {
    const acceptsNew = physician.accept_new_patients
    const acceptsRefill = physician.accept_refill_patients
    const minimizeScripts = physician.physician_config?.minimize_scripts
    
    // Priority 1: Phasing out (both accepting with minimize scripts on)
    if (acceptsNew && acceptsRefill && minimizeScripts) {
      return 'phasing-out'
    }
    
    // Priority 2: Active (New Yes + Refill Yes, minimize scripts off)
    if (acceptsNew && acceptsRefill) {
      return 'active'
    }
    
    // Priority 3: Inactive (New No + Refill No)
    if (!acceptsNew && !acceptsRefill) {
      return 'inactive'
    }
    
    // Mixed states - treat as partial
    return 'partial'
  }

  // Sort physicians by status priority
  const sortPhysiciansByStatus = (physicians, order = 'asc') => {
    const statusPriority = {
      'phasing-out': 1,  // Highest priority
      'active': 2,
      'partial': 3,
      'inactive': 4      // Lowest priority
    }
    
    return [...physicians].sort((a, b) => {
      const statusA = getPhysicianStatus(a)
      const statusB = getPhysicianStatus(b)
      const priorityA = statusPriority[statusA] || 999
      const priorityB = statusPriority[statusB] || 999
      
      if (order === 'desc') {
        return priorityB - priorityA
      }
      return priorityA - priorityB
    })
  }

  // Get physicians for a specific state with license type info
  const getPhysiciansForState = (stateCode) => {
    if (!physicians.length) return []

    const filteredPhysicians = physicians.filter(physician => {
      const activeLicenses = calculateActiveLicenses(physician.licenses)
      const hasStateMatch = activeLicenses.some(license => license.state === stateCode)
      return hasStateMatch
    })
    
    return filteredPhysicians.map(physician => {
      const activeLicenses = calculateActiveLicenses(physician.licenses)
      const stateLicenses = activeLicenses.filter(license => license.state === stateCode)
      const hasStateLicense = stateLicenses.some(license => license.type === 'state')
      const hasDEALicense = stateLicenses.some(license => license.type === 'DEA')
      
      return {
        name: physician.last_name,
        hasStateOnly: hasStateLicense && !hasDEALicense,
        hasDEA: hasDEALicense
      }
    })
  }

  // Create tooltip content for state
  const createTooltipContent = (stateCode, stateName) => {
    const statePhysicians = getPhysiciansForState(stateCode)
    
    if (statePhysicians.length === 0) {
      return `<div><strong>${stateName}</strong><br/>No physicians licensed</div>`
    }

    const deaPhysicians = statePhysicians.filter(p => p.hasDEA)
    const stateOnlyPhysicians = statePhysicians.filter(p => p.hasStateOnly)
    
    let content = `<div><strong>${stateName}</strong><br/>`
    
    if (deaPhysicians.length > 0) {
      content += `<span style="color: #9333ea;">● DEA Licensed:</span> ${deaPhysicians.map(p => p.name).join(', ')}<br/>`
    }
    
    if (stateOnlyPhysicians.length > 0) {
      content += `<span style="color: #16a34a;">● State Only:</span> ${stateOnlyPhysicians.map(p => p.name).join(', ')}`
    }
    
    content += `</div>`
    return content
  }

  // Apply sorting to physicians list
  const sortedPhysicians = (() => {
    if (sortBy === 'status') {
      return sortPhysiciansByStatus(physicians, sortOrder)
    }
    return physicians // Backend handles other sorting
  })()

  const formatLicenseCount = (physician) => {
    // Use our consistent frontend helper with raw license data
    const activeLicenses = calculateActiveLicenses(physician.licenses)
    const count = activeLicenses.length
    return count > 0 ? `${count} license${count === 1 ? '' : 's'}` : 'No licenses'
  }

  // Check if user needs to select a clinic
  if (!shared && !currentClinicId) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-yellow-800">Clinic Selection Required</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-yellow-800">
              Please select a clinic from the dropdown in the header to view physician information.
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className={bare ? 'space-y-4 sm:space-y-6' : 'container mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {stats ? `${stats.total_physicians} total physicians` : 'Manage and view physician information'}
            {physicians.length > 0 && ` • Showing ${physicians.length} on this page`}
          </p>
        </div>
        <div className="flex gap-2 self-start sm:self-auto">
          {!shared && user?.role === 'super_admin' && currentClinicId && (
            <Button
              variant="outline"
              onClick={() => setIsImportModalOpen(true)}
              className="gap-2"
              size="sm"
            >
              <span>📥</span>
              Import Physicians
            </Button>
          )}
          {canEditConfigs && (
            <Button
              onClick={() => {
                setEditingPhysician(null)
                setIsPhysicianModalOpen(true)
              }}
              className="gap-2"
              size="sm"
            >
              <span>➕</span>
              Add Physician
            </Button>
          )}
        </div>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-card-foreground">{stats.total_physicians}</div>
              <p className="text-xs text-muted-foreground">Total Physicians</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-card-foreground">{stats.accepting_new_patients}</div>
              <p className="text-xs text-muted-foreground">Accepting New Patients</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-card-foreground">{stats.active_licenses}</div>
              <p className="text-xs text-muted-foreground">Active Licenses</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-card-foreground">{stats.active_states?.length || 0}</div>
              <p className="text-xs text-muted-foreground">States Licensed</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Aggregated US Coverage Map */}
      {physicians.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>US License Coverage</CardTitle>
            <CardDescription>
              Physician license coverage across all states
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Legend */}
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#7c3aed' }}></div>
                <span>Active DEA (Highest Priority)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#16a34a' }}></div>
                <span>Active State License</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#eab308' }}></div>
                <span>Expiring Licenses</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#dc2626' }}></div>
                <span>Expired Licenses</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#e5e7eb' }}></div>
                <span>No Coverage</span>
              </div>
            </div>
            
            {/* US State Map */}
            <div className="relative border rounded-lg p-4 bg-slate-50 dark:bg-slate-900/50">
              <ComposableMap 
                projection="geoAlbersUsa"
                className="w-full h-64"
                width={800}
                height={500}
              >
                <Geographies geography={geoUrl}>
                  {({ geographies }) => {
                    const stateCoverage = calculateStateCoverage()
                    
                    return geographies.map((geo) => {
                      const stateName = geo.properties.NAME
                      const stateCode = getStateAbbreviation(stateName)
                      const color = getStateColor(stateCode, stateCoverage)
                      const coverage = stateCoverage[stateCode]
                      
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={color}
                          stroke="#374151"
                          strokeWidth={0.5}
                          onClick={() => handleStateClick(stateCode, stateName)}
                          data-tooltip-id="state-tooltip"
                          data-tooltip-html={createTooltipContent(stateCode, stateName)}
                          style={{
                            default: {
                              outline: "none",
                            },
                            hover: {
                              fill: color,
                              stroke: "#374151",
                              strokeWidth: 2,
                              outline: "none",
                            },
                            pressed: {
                              fill: color,
                              stroke: "#374151",
                              strokeWidth: 2,
                              outline: "none",
                            },
                          }}
                        >
                          {coverage && (
                            <title>
                              {stateCode}: {coverage.physicianCount} physician{coverage.physicianCount !== 1 ? 's' : ''}
                              {coverage.hasActiveDEA ? ' with active DEA licenses' : 
                               coverage.hasActiveState ? ' with active state licenses' : 
                               coverage.hasExpiring ? ' with expiring licenses' : 
                               coverage.hasExpired ? ' with expired licenses' : ''}
                            </title>
                          )}
                        </Geography>
                      )
                    })
                  }}
                </Geographies>
                
                {/* Add DC and Puerto Rico as custom boxes */}
                <g>
                  {/* Washington DC */}
                  <rect
                    x={720}
                    y={250}
                    width={15}
                    height={15}
                    fill={getStateColor('DC', calculateStateCoverage())}
                    stroke="#374151"
                    strokeWidth={0.5}
                    onClick={() => handleStateClick('DC', 'Washington DC')}
                    data-tooltip-id="state-tooltip"
                    data-tooltip-html={createTooltipContent('DC', 'Washington DC')}
                    style={{ cursor: 'pointer' }}
                  />
                  <text
                    x={727}
                    y={240}
                    textAnchor="middle"
                    fontSize={8}
                    fill="#374151"
                    className="pointer-events-none"
                  >
                    DC
                  </text>
                  
                  {/* Puerto Rico */}
                  <rect
                    x={680}
                    y={450}
                    width={25}
                    height={15}
                    fill={getStateColor('PR', calculateStateCoverage())}
                    stroke="#374151"
                    strokeWidth={0.5}
                    onClick={() => handleStateClick('PR', 'Puerto Rico')}
                    data-tooltip-id="state-tooltip"
                    data-tooltip-html={createTooltipContent('PR', 'Puerto Rico')}
                    style={{ cursor: 'pointer' }}
                  />
                  <text
                    x={692}
                    y={440}
                    textAnchor="middle"
                    fontSize={8}
                    fill="#374151"
                    className="pointer-events-none"
                  >
                    PR
                  </text>
                </g>
              </ComposableMap>
            </div>

            {/* Coverage Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {(() => {
                const coverage = calculateStateCoverage()
                const activeStates = Object.values(coverage).filter(c => c.hasActiveLicenses).length
                const issueStates = Object.values(coverage).filter(c => c.hasExpiring || c.hasExpired).length
                return (
                  <>
                    <div>
                      <div className="font-medium text-muted-foreground">States Covered:</div>
                      <div className="text-lg font-semibold">
                        {activeStates}
                      </div>
                    </div>
                    <div>
                      <div className="font-medium text-muted-foreground">Total Physicians:</div>
                      <div className="text-lg font-semibold">
                        {physicians.length}
                      </div>
                    </div>
                    <div>
                      <div className="font-medium text-muted-foreground">States with Issues:</div>
                      <div className="text-lg font-semibold text-yellow-600">
                        {issueStates}
                      </div>
                    </div>
                    <div>
                      <div className="font-medium text-muted-foreground">No Coverage:</div>
                      <div className="text-lg font-semibold text-gray-500">
                        {52 - activeStates} states
                      </div>
                    </div>
                  </>
                )
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder="Search by name, title, or specialties..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-background border-input text-foreground placeholder:text-muted-foreground"
              />
            </div>
            <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90">Search</Button>
          </form>
          
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-5">
            <div className="space-y-2">
              <Label htmlFor="title-filter">Title</Label>
              <Select value={titleFilter} onValueChange={setTitleFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Titles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Titles</SelectItem>
                  <SelectItem value="MD">MD</SelectItem>
                  <SelectItem value="DO">DO</SelectItem>
                  <SelectItem value="NP">NP</SelectItem>
                  <SelectItem value="PA">PA</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status-filter">Accepting Patients</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sort-filter">Sort By</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="status">Status Priority</SelectItem>
                  <SelectItem value="last_name">Last Name</SelectItem>
                  <SelectItem value="first_name">First Name</SelectItem>
                  <SelectItem value="created_at">Date Added</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="order-filter">Order</Label>
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger>
                  <SelectValue placeholder="Order" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Ascending</SelectItem>
                  <SelectItem value="desc">Descending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 col-span-2 md:col-span-1">
              <Label className="hidden sm:block">&nbsp;</Label>
              <Button variant="outline" onClick={resetFilters} className="w-full border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground">
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="text-red-800">
              <strong>Error:</strong> {error}
            </div>
          </CardContent>
        </Card>
      )}


      {/* Physicians Table */}
      <Card>
        <CardHeader>
          <CardTitle>Physicians</CardTitle>
          <CardDescription>
            {loading ? 'Loading...' : `${physicians.length} physicians found`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="text-lg">Loading physicians...</div>
            </div>
          ) : sortedPhysicians.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No physicians found matching your criteria.
            </div>
          ) : (
            <>
            {/* Mobile: Card layout */}
            <div className="sm:hidden space-y-2">
              {sortedPhysicians.map((physician, index) => {
                const acceptsNew = physician.accept_new_patients
                const acceptsRefill = physician.accept_refill_patients
                const minimizeScripts = physician.physician_config?.minimize_scripts
                const isActive = acceptsNew && acceptsRefill
                const isInactive = !acceptsNew && !acceptsRefill
                
                const isDraftCreate = physician._isDraft && physician._draftOperation === 'create'
                const mobileCardClass = `block border rounded-lg p-3 hover:bg-muted/50 transition-colors ${physician._isDraft ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-300' : ''}`
                const mobileCardKey = `physician-mobile-${physician._id || physician._draftId}-${index}`
                const cardInner = (
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-muted-foreground">{physician.title || '-'}</span>
                          <span className="font-medium truncate">{formatPhysicianName(physician)}</span>
                          {physician._isDraft && (
                            <Badge className="bg-amber-500 text-white text-xs shrink-0">
                              {physician._draftOperation === 'create' ? 'NEW' : 'DRAFT'}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {formatLicenseCount(physician)}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {isActive ? (
                          <Badge variant="success" className="text-xs">Active</Badge>
                        ) : isInactive ? (
                          <Badge variant="destructive" className="text-xs">Inactive</Badge>
                        ) : (
                          <Badge variant="gray" className="text-xs">Mixed</Badge>
                        )}
                        {minimizeScripts && (
                          <Badge variant="warning" className="text-xs">Phasing</Badge>
                        )}
                      </div>
                    </div>
                )
                return isDraftCreate ? (
                  <button
                    key={mobileCardKey}
                    type="button"
                    onClick={() => openDraftPhysician(physician)}
                    className={`${mobileCardClass} text-left w-full`}
                  >
                    {cardInner}
                  </button>
                ) : (
                  <Link
                    key={mobileCardKey}
                    to={`${basePath}/${physician._id}`}
                    className={mobileCardClass}
                  >
                    {cardInner}
                  </Link>
                )
              })}
            </div>

            {/* Desktop: Table layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4 font-medium text-muted-foreground">Title</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Name</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Licenses</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Patient</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Settings</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPhysicians.map((physician, index) => (
                    <tr 
                      key={`physician-${physician._id}-${index}`} 
                      className={`border-b border-border hover:bg-muted/50 ${physician._isDraft ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`}
                    >
                      {/* Title */}
                      <td className="p-4 text-sm font-medium text-muted-foreground">
                        {physician.title || '-'}
                      </td>
                      
                      {/* Name */}
                      <td className="p-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground">
                              {formatPhysicianName(physician)}
                            </span>
                            {physician._isDraft && (
                              <Badge className="bg-amber-500 text-white text-xs">
                                {physician._draftOperation === 'create' ? 'NEW' : 'DRAFT'}
                              </Badge>
                            )}
                          </div>
                          {physician.specialties && physician.specialties.length > 0 && (
                            <div className="text-sm text-muted-foreground">
                              {physician.specialties.slice(0, 2).join(', ')}
                              {physician.specialties.length > 2 && '...'}
                            </div>
                          )}
                        </div>
                      </td>
                      {/* Licenses */}
                      <td className="p-4 text-sm text-muted-foreground">
                        {formatLicenseCount(physician)}
                      </td>
                      {/* Patient Acceptance */}
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {(() => {
                            const acceptsNew = physician.accept_new_patients
                            const acceptsRefill = physician.accept_refill_patients
                            const minimizeScripts = physician.physician_config?.minimize_scripts
                            
                            // Active: New Yes + Refill Yes (green)
                            if (acceptsNew && acceptsRefill) {
                              return (
                                <>
                                  <Badge variant="success">
                                    Active
                                  </Badge>
                                  {minimizeScripts && (
                                    <Badge variant="warning">
                                      Phasing Out
                                    </Badge>
                                  )}
                                </>
                              )
                            }
                            
                            // Inactive: New No + Refill No (red)
                            if (!acceptsNew && !acceptsRefill) {
                              return (
                                <Badge variant="destructive">
                                  Inactive
                                </Badge>
                              )
                            }
                            
                            // Mixed states: show individual badges
                            return (
                              <>
                                <Badge variant={acceptsNew ? "success" : "gray"}>
                                  New: {acceptsNew ? 'Yes' : 'No'}
                                </Badge>
                                <Badge variant={acceptsRefill ? "success" : "gray"}>
                                  Refill: {acceptsRefill ? 'Yes' : 'No'}
                                </Badge>
                              </>
                            )
                          })()}
                        </div>
                      </td>
                      
                      {/* Settings */}
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          <Badge variant={physician.physician_config?.minimize_scripts ? "purple" : "gray"}>
                            Min Scripts: {physician.physician_config?.minimize_scripts ? 'On' : 'Off'}
                          </Badge>
                        </div>
                      </td>
                      {/* Actions */}
                      <td className="p-4">
                        {physician._isDraft && physician._draftOperation === 'create' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openDraftPhysician(physician)}
                            className="border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground"
                          >
                            Edit Draft
                          </Button>
                        ) : (
                          <Link to={`${basePath}/${physician._id}`}>
                            <Button variant="outline" size="sm" className="border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground">
                              View Details
                            </Button>
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {((pagination.current_page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.current_page * pagination.limit, pagination.total_count)} of{' '}
                {pagination.total_count} physicians
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={pagination.current_page <= 1}
                  className="border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                >
                  Previous
                </Button>
                
                <span className="text-sm text-muted-foreground">
                  Page {pagination.current_page} of {pagination.total_pages}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(pagination.total_pages, prev + 1))}
                  disabled={pagination.current_page >= pagination.total_pages}
                  className="border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* State Physicians Modal */}
      <Dialog open={isStateModalOpen} onOpenChange={setIsStateModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Physicians with Active Licenses in {selectedState?.name} ({selectedState?.code})
            </DialogTitle>
            <DialogDescription>
              {statePhysicians.length} physician{statePhysicians.length !== 1 ? 's' : ''} found with active licenses in {selectedState?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {statePhysicians.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No physicians found with active licenses in {selectedState?.name}.
              </div>
            ) : (
              <div className="space-y-3">
                {statePhysicians.map((physician) => (
                  <Card key={physician.id} className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        {/* Physician Name and Basic Info */}
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-lg">
                              {physician.fullName}
                            </h3>
                            {physician.npi && (
                              <div className="text-sm text-muted-foreground">
                                NPI: {physician.npi}
                              </div>
                            )}
                          </div>
                          <Link 
                            to={`${basePath}/${physician.id}`}
                            className="text-primary hover:text-primary/80 text-sm font-medium"
                          >
                            View Details →
                          </Link>
                        </div>
                        
                        {/* License Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                          {physician.stateLicense && (
                            <div className="bg-muted/50 p-3 rounded-lg">
                              <div className="font-medium text-sm text-foreground">State Medical License</div>
                              <div className="text-sm font-mono">{physician.stateLicense}</div>
                              {physician.stateLicenseExpiry && (
                                <div className="text-xs text-muted-foreground">
                                  Expires: {physician.stateLicenseExpiry}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {physician.deaLicense && (
                            <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                              <div className="font-medium text-sm text-purple-700 dark:text-purple-300">DEA License</div>
                              <div className="text-sm font-mono text-purple-800 dark:text-purple-200">{physician.deaLicense}</div>
                              {physician.deaLicenseExpiry && (
                                <div className="text-xs text-purple-600 dark:text-purple-400">
                                  Expires: {physician.deaLicenseExpiry}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Patient Acceptance & Configuration */}
                        <div className="space-y-2">
                          <div className="text-sm font-medium">Patient Acceptance & Settings</div>
                          <div className="flex flex-wrap gap-2">
                            {/* Status Badge */}
                            {physician.status === 'active' && (
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                Active
                              </Badge>
                            )}
                            {physician.status === 'phasing-out' && (
                              <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                                Phasing Out
                              </Badge>
                            )}
                            {physician.status === 'inactive' && (
                              <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                Inactive
                              </Badge>
                            )}
                            
                            {/* Patient acceptance */}
                            <Badge variant={physician.acceptNewPatients ? "default" : "secondary"}>
                              New: {physician.acceptNewPatients ? 'Yes' : 'No'}
                            </Badge>
                            <Badge variant={physician.acceptRefillPatients ? "default" : "secondary"}>
                              Refill: {physician.acceptRefillPatients ? 'Yes' : 'No'}
                            </Badge>
                            
                            {/* Script minimization */}
                            {physician.minimizeScripts && (
                              <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                                Min Scripts: On
                              </Badge>
                            )}
                            
                            {/* License Type Indicator */}
                            {physician.hasDEA && (
                              <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                DEA Licensed
                              </Badge>
                            )}
                            {physician.hasStateOnly && (
                              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                State Only
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Tooltip for state hover */}
      <Tooltip 
        id="state-tooltip" 
        place="top"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          color: 'white',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '12px',
          maxWidth: '300px',
          zIndex: 1000
        }}
      />
      
      {/* Physician Add/Edit Modal */}
      <PhysicianEditModal
        isOpen={isPhysicianModalOpen}
        onClose={() => {
          setIsPhysicianModalOpen(false)
          setEditingPhysician(null)
          setEditingDraftId(null)
        }}
        physician={editingPhysician}
        isDraftEdit={!!editingDraftId}
        {...sharedDraftProps}
        onSave={(updatedData) => {
          if (editingDraftId) {
            updateChange(editingDraftId, { new_data: updatedData })
          }
          // Reload physicians list to pick up draft / DB changes
          loadPhysicians()
        }}
      />

      {/* Import Physicians Modal (super_admin only) */}
      <ImportPhysiciansModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        currentClinicId={currentClinicId}
        onImport={() => {
          loadPhysicians()
        }}
      />
    </div>
  )
}
/**
 * A clinic whose provider policy isn't local_only routes from core's shared
 * directory, so that's what this page shows; its own physicians collection is
 * unused and the API refuses edits to it. Editing the directory needs the
 * Shared doctor directory permission.
 */
export default function PhysiciansPage() {
  const { currentClinicId } = useAuthStore()
  const [source, setSource] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setSource(null)
    if (!currentClinicId) return
    physicianAPI.source()
      .then(r => { if (!cancelled) setSource(r.data.data) })
      // If core can't say, show the local page; its writes are still guarded server-side.
      .catch(() => { if (!cancelled) setSource({ source: 'unknown', shared: false }) })
    return () => { cancelled = true }
  }, [currentClinicId])

  if (currentClinicId && !source) {
    return <div className="container mx-auto p-6 text-sm text-muted-foreground">Loading…</div>
  }
  if (!source?.shared) return <PhysiciansDirectory />

  return (
    <div className="container mx-auto space-y-4 p-4 sm:p-6">
      <Card className="border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        This clinic routes from the <strong>shared doctor directory</strong> (<code>{source.source}</code>), not its own physician list.
        {source.can_edit_shared
          ? ' Edits here are drafted to the shared directory and, once published, apply to every clinic using it.'
          : ' It is read-only here.'}
      </Card>
      {source.can_edit_shared && <SharedDraftBar onPublished={() => setReloadKey(k => k + 1)} />}
      <PhysiciansDirectory key={reloadKey} shared canEdit={!!source.can_edit_shared} bare />
    </div>
  )
}
